import { AttachmentPreviewMethod } from 'owa-attachment-data';
import { SaveToCloudStatus } from 'owa-attachment-savetocloud';
import { ClientItemId } from 'owa-client-ids';
import GetAttachmentTextPreviewReadMode from 'owa-service/lib/contract/GetAttachmentTextPreviewReadMode';
import { lazyGetSxSViewStateFromElement } from 'owa-sxs';
import {
    lazyOnPreviewOperationFinished,
    lazyOnPreviewOperationStart,
    lazyShowSxS,
    onSxSChangeOrClose,
    PreviewPaneSelectionSource,
    SxSCustomization,
    getOrCreateSxSStoreData,
} from 'owa-sxs-store';
import { ImageGalleryItem, lazySetImagePreviewState, SxSViewState } from 'owa-sxsdata';
import { SxSReadingPaneInitializeMethod } from '../types/SxSReadingPaneStrategy';
import insertSxSV2IntoDomHelper from '../utils/insertSxSV2IntoDomHelper';
import { throwUserError } from '../utils/throwUserError';
import { initializeReadingPaneInSxS } from './internalActions';
import { lazyGetAttachment } from 'owa-attachment-model-store';
import { getUserAttachmentPolicyChecker } from 'owa-attachment-policy';
import { getSxSIdFromProjection } from 'owa-tab-store';
import { isFeatureEnabled } from 'owa-feature-flags';

async function getSxSViewStateInternal(
    target: HTMLElement | SxSViewState
): Promise<SxSViewState | null> {
    let sxsViewState: SxSViewState = null;
    if (isFeatureEnabled('rp-projectionPopout') && isFeatureEnabled('doc-SxS-multipleViews')) {
        if ((target as HTMLElement)?.tagName?.toUpperCase() === 'IMG') {
            const getSxSViewState = await lazyGetSxSViewStateFromElement.import();
            sxsViewState = getSxSViewState(target as HTMLElement);
        } else {
            sxsViewState = target as SxSViewState;
        }
    } else {
        if (target instanceof HTMLElement) {
            const getSxSViewState = await lazyGetSxSViewStateFromElement.import();
            sxsViewState = getSxSViewState(target);
        } else {
            sxsViewState = target;
        }
    }
    return sxsViewState;
}

export default async function previewImageInSxS(
    item: ImageGalleryItem,
    imageGallery: ImageGalleryItem[],
    parentItemId: ClientItemId,
    target: HTMLElement | SxSViewState
) {
    const targetWindow: Window = (target as HTMLElement)?.ownerDocument?.defaultView || window;
    const sxsId: string =
        targetWindow === window ? 'primaryTab' : getSxSIdFromProjection(targetWindow);
    const isProjectionPopOut: boolean = targetWindow !== window;

    insertSxSV2IntoDomHelper.run(targetWindow, sxsId);

    const sxsStore = getOrCreateSxSStoreData(sxsId);
    const onPreviewOperationStart = await lazyOnPreviewOperationStart.import();
    const onPreviewOperationFinished = await lazyOnPreviewOperationFinished.import();

    let sxsViewState: SxSViewState = await getSxSViewStateInternal(target);

    if (!sxsViewState) {
        sxsViewState = sxsStore.viewState;
    }

    const optionalParameters = {
        downloadUrl: item.downloadUrl,
        downloadShouldOpenNewTab: false,
        useHighContrastCommandBar: false,
        id: item.id,
    };

    const currentPendingOperation = {
        id: `${new Date().getTime()}_${Math.floor(Math.random() * 1000)}`,
        attachmentId: item.id,
    };
    onPreviewOperationStart(currentPendingOperation, sxsId);

    try {
        // do not need to wait for edit mode/compose as this function should only be triggered from reading pane

        const showSxS = await lazyShowSxS.import();
        const setImagePreviewState = await lazySetImagePreviewState.import();
        const getAttachment = await lazyGetAttachment.import();
        const allowDownload = getAttachment(item.id)?.info.allowDownload;

        if (
            !sxsStore.pendingPreviewOperation ||
            currentPendingOperation.id !== sxsStore.pendingPreviewOperation.id
        ) {
            throwUserError('Preview changed before loading finished');
        }

        initializeReadingPaneInSxS(
            {
                method: SxSReadingPaneInitializeMethod.CopyMailReadingPaneContainer,
            },
            PreviewPaneSelectionSource.ReadingPane,
            null /* parentItemId */,
            null /* instrumentationContext */,
            sxsId,
            isProjectionPopOut
        );
        setImagePreviewState(
            sxsViewState,
            item.previewUrl,
            imageGallery,
            allowDownload,
            !allowDownload && !getUserAttachmentPolicyChecker().printWithoutDownloadEnabled,
            optionalParameters
        );
        showSxS(
            sxsStore.shouldShow ? null : SxSCustomization.Mail,
            {
                previewMode: AttachmentPreviewMethod.Image,
                attachmentId: item.id,
                selectionSource: PreviewPaneSelectionSource.ReadingPane,
                isCloudy: false,
                saveToCloudStatus: SaveToCloudStatus.disabled,
                textPreviewReadMode: GetAttachmentTextPreviewReadMode.AutoDetect,
            },
            {
                editAllowed: false,
                itemId: parentItemId,
                isDraft: false,
            },
            null, // selectedLinkId
            sxsId,
            isProjectionPopOut
        );
        onSxSChangeOrClose(null);
        onPreviewOperationFinished(sxsId);
    } catch (error) {}
}
